import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import AutoSizer from 'react-virtualized-auto-sizer';

import taskTableTheme from './Theme';
import {SortableTreeWithoutDndContext as SortableTree} from '@forecasthq/react-virtualized-nested-dnd';
import Styled from 'styled-components/macro';
import {useIntl} from 'react-intl';
import {PhaseHeaderRow} from './elements/PhaseHeaderRow';
import {canNodeHaveChildren} from './table-util/TableLogic';
import {alignToFlex} from './elements/utils';
import {EXPANSION_COLUMN_WIDTH, ExpansionWrapper} from './elements/Elements.styled';
import {EMPTY_STATE} from '../../../../../constants';
import EmptyState from '../../empty-states/empty_state';
import {PeriodRow} from './elements/PeriodRow';
import {NodeContentRenderer, TYPES} from './Theme/NodeContentRenderer';
import {Scrollbar} from '@forecast-it/design-system';
import {FinancialCategoryTooltip} from '@/forecast-app/shared/components/tooltips/FinancialCategoryTooltip';
import {NOVA_INVOCATION_COLUMN_WIDTH} from '../../nova-invocation/styleTokens';
import {isNovaInvocationEnabled} from '../../nova-invocation/novaInvocationGate';

const TaskTableWrapper = Styled.div`
	width: ${props => props.styleWidth}px;
	height: ${props => props.styleHeight}px;
	margin: 0 32px;
	.ReactVirtualized__Grid {
		outline: none;
	}
`;

export const GROUP_TYPE = {
	NO_GROUPING: 'no_grouping',
	ROLE: 'role',
	PERSON: 'person',
};

export const TaskTable = React.memo(
	({
		newTopNode = null,
		availableColumns = [],
		tableData = [],
		selectedTasks = [],
		setSelectedTasks = () => null,
		onMoveNode = () => null,
		onVisibilityToggle = () => null,
		canDrop = () => true,
		canDrag = () => true,
		slideRegionSize = 150,
		groupType,
		showBaselineInfo = false,
		useTaskHierarchy,
		onToggleExpansion = () => null,
		showNovaColumn: showNovaColumnPreference,
	}) => {
		//useWhyDidYouUpdate('Task Table', {availableColumns, tableData, onMoveNode, onVisibilityToggle, canDrag, canDrop, slideRegionSize, groupType});
		const [topNodeId, setTopNodeId] = useState(undefined);

		// Used for handling the scrollbar Width for the top fixed header calculation.
		const [scrollbarWidth, setScrollbarWidth] = useState(0);

		// The feature decides whether the column can be shown at all; the person's own view options
		// decide whether it is. The preference comes from the caller, which owns the eye menu this
		// table is rendered under — an absent one reads as shown, so a caller that does not offer the
		// option keeps the column rather than losing it to a preference nobody expressed.
		//
		// The column used to hide itself for a viewer whose every control would have been hidden
		// anyway. That no longer describes anything: a person who may not act sees the control
		// disabled with its reason, never an absence, so there is no such thing as a column that
		// would render empty. A reason somebody cannot act on is still a reason, and stating it is
		// the point.
		const showNovaColumn = useMemo(
			() => isNovaInvocationEnabled() && showNovaColumnPreference !== false,
			[showNovaColumnPreference]
		);

		// SortableTree's theme.nodeContentRenderer is a plain component reference with no way to
		// pass extra per-render data through the tree library itself — wrapping it here is how
		// showNovaColumn (the same value the header above uses) reaches each row, so the two can
		// never compute it independently and disagree.
		const theme = useMemo(
			() => ({
				...taskTableTheme,
				nodeContentRenderer: props => <NodeContentRenderer {...props} showNovaColumn={showNovaColumn} />,
			}),
			[showNovaColumn]
		);

		// Width calculation
		const listWidth = useMemo(() => {
			// Start with 14 from draghandle, 48 from Margin, and — when the Nova column is shown —
			// the Nova invocation column each row renders in its left cluster. That column is
			// TaskRow.js's own, not one of availableColumns, so it isn't counted by the loop below.
			// Neither is the expansion toggle/placeholder each row renders under task hierarchy.
			let totalWidth =
				62 + (showNovaColumn ? NOVA_INVOCATION_COLUMN_WIDTH : 0) + (useTaskHierarchy ? EXPANSION_COLUMN_WIDTH : 0);
			availableColumns.forEach(column => {
				if (column.checked) {
					totalWidth += column.width;
				}
			});
			return totalWidth;
		}, [availableColumns, showNovaColumn, useTaskHierarchy]);

		/**
		 * This method will return the top node component base on the type.
		 *
		 * It currently supports Phase headers and Periods.
		 *
		 * @param topNode The top node to show
		 * @returns {JSX.Element|null} The JSX component
		 */
		const topNodeComponent = topNode => {
			switch (topNode?.type) {
				case TYPES.PERIOD:
					return <PeriodRow node={topNode} />;
				case TYPES.HEADER:
					return (
						<PhaseHeaderRow
							topHeader={true}
							node={topNode}
							onClick={() => onToggleExpansion({node: topNode})}
							cy="top-node"
							showNovaColumn={showNovaColumn}
						/>
					);
				default:
					return null;
			}
		};

		/**
		 * This method handles scrolling in the table. Setting the top node to the
		 * new ID when reaching it.
		 *
		 * @param node The node to set, if it matches the top node type.
		 */
		const handleTopRowRender = node => {
			if (node?.topNodeId) {
				setTopNodeId(node.topNodeId);
			}

			// Calculate the scrollbar width using the grid from the Sortable Tree
			const gridNode = document.querySelector('.ReactVirtualized__Grid');
			setScrollbarWidth(gridNode ? gridNode.offsetWidth - gridNode.clientWidth : 0);
		};

		useEffect(() => {
			if (newTopNode) {
				setTopNodeId(newTopNode);
			}
		}, [newTopNode]);

		/**
		 * Returns the top node
		 */
		const topNode = topNodeId && tableData.find(treeNode => treeNode.id === topNodeId);

		const getNodeKey = ({node}) => node.id;

		const minimumDropDepth = useMemo(() => {
			switch (groupType) {
				case GROUP_TYPE.NO_GROUPING:
					return 1;
				case GROUP_TYPE.ROLE:
				case GROUP_TYPE.PERSON:
					return 2;
				default:
					return 1;
			}
		}, [groupType]);

		if (tableData.length === 0) {
			return <EmptyState pageName={EMPTY_STATE.SCOPING} />;
		}

		return (
			<Scrollbar hideAxis={Scrollbar.Axis.Vertical} hasFocusableContent isPageScroller>
				<AutoSizer style={{width: '100%'}}>
					{({height, width}) => {
						const innerListWidth = (listWidth > width ? listWidth : width) - 64; // Subtract 64px of padding
						return (
							<TaskTableWrapper styleWidth={innerListWidth} styleHeight={height}>
								<div
									style={{
										width: innerListWidth,
										height,
										display: 'flex',
										flexDirection: 'column',
										position: 'relative',
									}}
								>
									<TableHeader
										availableColumns={availableColumns}
										scrollbarWidth={scrollbarWidth}
										showNovaColumn={showNovaColumn}
										useTaskHierarchy={useTaskHierarchy}
										tableData={tableData}
									/>
									{!!topNode && (
										<div
											style={{
												height: '48px',
												padding: '4px 0',
												backgroundColor: '#fff',
												width: innerListWidth - scrollbarWidth,
												position: 'absolute',
												display: 'flex',
												alignItems: 'center',
												top: '38px',
												zIndex: 2,
											}}
											data-userpilot={'milestone-first'}
										>
											{topNodeComponent(topNode)}
										</div>
									)}
									{/* the 38px is from the table header*/}
									<div style={{width: innerListWidth, height: height - 38}} data-cy="task-table-wrapper">
										<SortableTree
											// SortableTree renders through react-virtualized, which caches
											// its rows and re-renders them when its own props or the tree
											// data change — not when the theme it was handed produces a
											// different node renderer. Showing or hiding Nova changes only
											// the latter, so without a key that moves with it the header
											// cell disappeared while every row kept its control, leaving
											// the two a column out of step. Keyed rather than pushed through
											// the list's props because this is a remount by intent: the
											// rows are a different shape afterwards.
											key={`nova-column-${showNovaColumn}`}
											onTopRowRender={handleTopRowRender}
											minimumDropDepth={minimumDropDepth}
											treeData={tableData}
											onMoveNode={dragInfo =>
												onMoveNode(dragInfo, selectedTasks, groupType, setSelectedTasks)
											}
											onVisibilityToggle={onVisibilityToggle}
											canNodeHaveChildren={node =>
												canNodeHaveChildren(node, showBaselineInfo, useTaskHierarchy)
											}
											canDrag={canDrag}
											canDrop={canDrop}
											slideRegionSize={slideRegionSize}
											theme={theme}
											getNodeKey={getNodeKey}
											onChange={() => null}
											reactVirtualizedListProps={{
												style: {outline: 'none'},
												width: innerListWidth,
											}}
										/>
									</div>
								</div>
							</TaskTableWrapper>
						);
					}}
				</AutoSizer>
			</Scrollbar>
		);
	}
);

TaskTable.propTypes = {
	availableColumns: PropTypes.array.isRequired,
	tableData: PropTypes.array.isRequired,
	onMoveNode: PropTypes.func,
	onVisibilityToggle: PropTypes.func,
	canDrop: PropTypes.func,
	canDrag: PropTypes.func,
	slideRegionSize: PropTypes.func,
	groupType: PropTypes.oneOf(Object.values(GROUP_TYPE)),
};

const HeaderFillerElement = Styled.div`
	width: ${props => props.width}px;
`;

export const TableHeaderStyle = Styled.div`
	padding: 8px 0;
	display: flex;
`;

export const HeaderTitleStyle = Styled.div`
	display: flex;
	padding-left: 8px;
	padding-right: ${props => props.paddingRight && `${props.paddingRight}px`};
	height: 22px;
	align-items: center;
	flex-grow: ${props => props.growable && '1'};
	width: ${props => !props.growable && `${props.width}px`};
	min-width: ${props => props.growable && `${props.width}px`};
	justify-content: ${props => alignToFlex(props.align)};
	text-align: ${props => props.align};
	font-size: 9px;
	letter-spacing: 1px;
	text-transform: uppercase;
	color: #A1A1A1;
`;

// The shallowest task anywhere in the tree, or Infinity where it holds none.
const shallowestTaskLevel = nodes =>
	nodes.reduce((shallowest, node) => {
		const own = node.type === TYPES.TASK ? node.level || 0 : Infinity;
		const below = node.children?.length ? shallowestTaskLevel(node.children) : Infinity;
		return Math.min(shallowest, own, below);
	}, Infinity);

/**
 * How far the tree indents the task rows this header stands over.
 *
 * The tree puts one scaffold block in front of a row per level of nesting, and none in front of
 * this header, so the header has to reserve the same. One block is only right for a task sitting
 * directly under its phase: group by role or person and the tasks are a level deeper, which would
 * leave every label in the leading cluster a block to the left of what it names. Read off the rows
 * themselves rather than assumed.
 *
 * The shallowest task wins, because that is the row whose columns the labels sit over; a subtask
 * below it is indented further and takes the width back out of its own name, which is how
 * everything after the name stays in line whatever the depth.
 *
 * Searched through children rather than across the top level: this is the nested tree the sortable
 * tree is handed, so its top level holds the phase and baseline rows and every task hangs under
 * one of them. A scan of the top level alone finds no task at all and silently falls back, which
 * is the whole bug this exists to fix.
 */
export const taskIndentWidth = (tableData, scaffoldBlockPxWidth) => {
	const level = shallowestTaskLevel(tableData);
	// One block where there are no task rows to measure, which is the depth a task directly under a
	// phase sits at - the common case, and the only sensible guess for a table that has nothing in
	// it yet.
	return (Number.isFinite(level) ? level : 1) * scaffoldBlockPxWidth;
};

export const TableHeader = ({
	availableColumns,
	scrollbarWidth,
	showNovaColumn = false,
	useTaskHierarchy = false,
	tableData = [],
}) => {
	const intl = useIntl();
	const selectorColumn = availableColumns.find(column => column.name === 'selector');
	// What a task row draws before its Nova column, which this header has to reserve exactly: the
	// tree's own indent for a task nested under a phase, the drag handle, and the selector checkbox
	// where it is enabled. None of the three has a header label of its own.
	//
	// The indent is the one that is easy to miss, because nothing in this header draws it: the tree
	// puts a scaffold block in front of every nested row and none in front of this one, so a header
	// that counted only its own columns left every label in the leading cluster a block to the left
	// of the thing it names - NOVA sitting over the checkbox rather than over the Action chip.
	//
	// The indent is taken from the rows themselves rather than assumed to be one block: tasks sit a
	// level deeper when the phase is grouped by role or person, and a level shallower on a table
	// that has no phases at all.
	//
	// The block width is read off the theme the tree is given rather than restated, so the two
	// cannot drift - and never passed back to the tree as a prop, which would override the theme
	// rather than agree with it.
	const leadingWidth =
		taskIndentWidth(tableData, taskTableTheme.scaffoldBlockPxWidth) +
		14 +
		(selectorColumn?.checked ? selectorColumn.width : 0);
	return (
		<TableHeaderStyle>
			<HeaderFillerElement width={leadingWidth} />
			{showNovaColumn && (
				<HeaderTitleStyle align="left" width={NOVA_INVOCATION_COLUMN_WIDTH}>
					{intl.formatMessage({id: 'nova_invocation.column.header'})}
				</HeaderTitleStyle>
			)}
			{/* Rows render an expansion toggle (or an equally wide placeholder) between the Nova
			    column and the task id under task hierarchy — without the same reservation here the
			    header drifts out of alignment once the row runs short on horizontal space. */}
			{useTaskHierarchy && <ExpansionWrapper />}
			<HeaderTitleStyle align="left" growable width={200} />
			{availableColumns
				.filter(
					column =>
						!(
							column.name === 'task-id' ||
							column.name === 'task-name' ||
							column.name === 'empty' ||
							column.name === 'expand' ||
							column.name === 'selector'
						) && column.width > 0
				)
				.map(column => {
					const props = {
						align: column.align || 'left',
						width: column.width,
						paddingRight: column.name === 'forecast' && 16,
					};
					return (
						column.checked && (
							<FinancialCategoryTooltip
								key={column.name}
								disabled={!column.getTooltipItems}
								items={column.getTooltipItems && column.getTooltipItems(intl.formatMessage)}
								size={'l'}
							>
								<HeaderTitleStyle {...props}>
									{!!column.translationId && intl.formatMessage({id: column.translationId})}
									{column.displayName}
								</HeaderTitleStyle>
							</FinancialCategoryTooltip>
						)
					);
				})}
			{scrollbarWidth ? <HeaderFillerElement width={scrollbarWidth} /> : null}
		</TableHeaderStyle>
	);
};
